import { snapToPixel } from '../util/math.js';
import { fieldMetrics } from '../field/metrics.js';
import type { ArcadeReplayFrameSnapshot, ArcadeReplayPackage } from '../../arcade/contracts.js';
import {
  applyArcadeReplayFrameToPlayers,
  blendArcadeReplayPlayersFromShell,
  defaultArcadeOwnerBallPointForReplayFrame,
  mapArcadeFieldXToWorldX,
  mapArcadeFieldYToWorldY,
} from './arcade-render.js';

const KICKOFF_RUNUP_FRAMES = 18;
const KICKOFF_LAUNCH_BLEND_FRAMES = 6;
const KICKOFF_KICKER_SETTLE_FRAMES = 8;
const OPENING_KICKOFF_PAUSE_FRAMES = 30;

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function blendBallPoint(from: any, to: any, amount: number) {
  const blend = clamp01(amount);
  const motionBlend = blend < 0.45 ? 0 : smoothstep((blend - 0.45) / 0.55);
  return {
    ballX: snapToPixel(from.ballX + (to.ballX - from.ballX) * motionBlend),
    ballY: snapToPixel(from.ballY + (to.ballY - from.ballY) * motionBlend),
    arcadeHeight: (from.arcadeHeight ?? 0)
      + ((to.arcadeHeight ?? 0) - (from.arcadeHeight ?? 0)) * motionBlend,
    arcadeGroundY: snapToPixel(
      (from.arcadeGroundY ?? from.ballY + 4)
        + ((to.arcadeGroundY ?? to.ballY + 4) - (from.arcadeGroundY ?? from.ballY + 4)) * motionBlend,
    ),
    arcadeState: blend >= 0.45 ? (to.arcadeState ?? from.arcadeState) : from.arcadeState,
    arcadeOwnerRole: blend >= 0.58 ? (to.arcadeOwnerRole ?? from.arcadeOwnerRole) : from.arcadeOwnerRole,
  };
}

function smoothstep(value: number) {
  const clamped = clamp01(value);
  return clamped * clamped * (3 - 2 * clamped);
}

function kickoffRunupProgress(frame: number) {
  const progress = clamp01(frame / Math.max(1, KICKOFF_RUNUP_FRAMES));
  if (progress <= 0.6) {
    return smoothstep(progress / 0.6) * 0.72;
  }
  return 0.72 + smoothstep((progress - 0.6) / 0.4) * 0.28;
}

function kickoffTeeBallPoint(
  replayFrame: ArcadeReplayFrameSnapshot,
  direction: number,
  metrics: ReturnType<typeof fieldMetrics>,
  renderedPlayers: any[] = [],
) {
  const kicker = renderedPlayers.find((player) => player.role === 'KICKER');
  if (!kicker) {
    return null;
  }

  return {
    ballX: mapArcadeFieldXToWorldX(replayFrame.ball.x, direction),
    ballY: snapToPixel(kicker.y + 20),
    arcadeHeight: 0,
    arcadeGroundY: snapToPixel(kicker.y + 24),
    arcadeState: 'kick-tee',
    arcadeOwnerRole: 'KICKER',
  };
}

function kickoffKickerRunupPoint(
  shellKicker: any,
  teePoint: any,
  direction: number,
  frame: number,
) {
  const runupProgress = kickoffRunupProgress(frame);
  const contactX = teePoint.ballX - direction * 10;
  const targetX = contactX;
  const targetY = snapToPixel(shellKicker.y - Math.sin(runupProgress * Math.PI) * 0.35);
  return {
    x: snapToPixel(shellKicker.x + (targetX - shellKicker.x) * runupProgress),
    y: targetY,
  };
}

function ballPointForReplayFrame(
  replayFrame: ArcadeReplayFrameSnapshot,
  direction: number,
  metrics: ReturnType<typeof fieldMetrics>,
  renderedPlayers: any[] = [],
) {
  const ownerPoint = defaultArcadeOwnerBallPointForReplayFrame(replayFrame, direction, metrics, renderedPlayers, { handYOffset: 8 });
  if (ownerPoint) {
    return ownerPoint;
  }

  const groundBallY = mapArcadeFieldYToWorldY(replayFrame.ball.y, metrics) + 8;
  const flightLift = replayFrame.ball.state === 'kick-flight'
    ? replayFrame.ball.z * 18
    : replayFrame.ball.state === 'onside-chip'
      ? replayFrame.ball.z * 14
    : replayFrame.ball.state === 'kick-bounce'
      ? replayFrame.ball.z * 9
    : replayFrame.ball.z * 4;
  return {
    ballX: mapArcadeFieldXToWorldX(replayFrame.ball.x, direction),
    ballY: snapToPixel(groundBallY - flightLift),
    arcadeHeight: Math.max(0, replayFrame.ball.z),
    arcadeGroundY: snapToPixel(groundBallY + 4),
    arcadeState: replayFrame.ball.state,
    arcadeOwnerRole: replayFrame.ball.ownerRendererRole,
  };
}

function buildKickoffLeadInFrames(
  context: any,
  replay: ArcadeReplayPackage,
  rawFrames: any[],
  metrics: ReturnType<typeof fieldMetrics>,
) {
  if (!rawFrames.length) {
    return rawFrames;
  }

  const firstReplayFrame = replay.frames[0];
  const firstRawFrame = rawFrames[0];
  const shellKicker = context.formation.offense.find((player: any) => player.role === 'KICKER');
  const teePoint = kickoffTeeBallPoint(firstReplayFrame, context.direction, metrics, firstRawFrame.offensePlayers);
  if (!shellKicker || !teePoint) {
    return rawFrames;
  }

  const contactPoint = kickoffKickerRunupPoint(shellKicker, teePoint, context.direction, KICKOFF_RUNUP_FRAMES);
  const settlePoint = {
    x: snapToPixel(teePoint.ballX),
    y: snapToPixel(shellKicker.y + 2),
  };
  const preKickFrames = Array.from({ length: KICKOFF_RUNUP_FRAMES }, (_, index) => {
    const frameNumber = index + 1;
    const runupPoint = kickoffKickerRunupPoint(shellKicker, teePoint, context.direction, frameNumber);
    return {
      offensePlayers: firstRawFrame.offensePlayers.map((player: any) => player.role === 'KICKER'
        ? {
            ...player,
            x: runupPoint.x,
            y: runupPoint.y,
            moving: frameNumber > 1,
          }
        : { ...player, moving: false }),
      defensePlayers: firstRawFrame.defensePlayers.map((player: any) => ({ ...player, moving: false })),
      ball: { ...teePoint },
    };
  });

  const launchFrames = rawFrames.slice(0, KICKOFF_LAUNCH_BLEND_FRAMES).map((frame: any, index: number) => {
    const launchBlend = smoothstep((index + 1) / Math.max(1, KICKOFF_LAUNCH_BLEND_FRAMES));
    const blendedKicker = {
      x: snapToPixel(contactPoint.x + (settlePoint.x - contactPoint.x) * launchBlend),
      y: snapToPixel(contactPoint.y + (settlePoint.y - contactPoint.y) * launchBlend),
    };
    return {
      offensePlayers: frame.offensePlayers.map((player: any) => player.role === 'KICKER'
        ? {
            ...player,
            x: blendedKicker.x,
            y: blendedKicker.y,
            moving: true,
          }
        : player),
      defensePlayers: frame.defensePlayers,
      ball: blendBallPoint(teePoint, frame.ball, launchBlend),
    };
  });

  const settleFrames = rawFrames.slice(KICKOFF_LAUNCH_BLEND_FRAMES, KICKOFF_LAUNCH_BLEND_FRAMES + KICKOFF_KICKER_SETTLE_FRAMES).map((frame: any, index: number) => {
    const settleBlend = smoothstep((index + 1) / Math.max(1, KICKOFF_KICKER_SETTLE_FRAMES));
    const blendedKicker = {
      x: snapToPixel(settlePoint.x + (settlePoint.x - settlePoint.x) * settleBlend),
      y: snapToPixel(settlePoint.y + (settlePoint.y - settlePoint.y) * settleBlend),
    };
    return {
      offensePlayers: frame.offensePlayers.map((player: any) => player.role === 'KICKER'
        ? {
            ...player,
            x: blendedKicker.x,
            y: blendedKicker.y,
            moving: index < 2,
          }
        : player),
      defensePlayers: frame.defensePlayers,
      ball: frame.ball,
    };
  });

  return [
    ...preKickFrames,
    ...launchFrames,
    ...settleFrames,
    ...rawFrames.slice(KICKOFF_LAUNCH_BLEND_FRAMES + KICKOFF_KICKER_SETTLE_FRAMES),
  ];
}

export function buildArcadeKickoffLiveSequence(context: any): any[] {
  const replay = context.play?.arcadeReplay as ArcadeReplayPackage | undefined;
  if (!replay?.frames?.length) {
    return [];
  }

  const metrics = fieldMetrics();
  const rawFrames = replay.frames.map((replayFrame) => {
    const offensePlayers = blendArcadeReplayPlayersFromShell(
      context.formation.offense,
      applyArcadeReplayFrameToPlayers(context.formation.offense, replayFrame, context.direction, metrics, {
        movingThreshold: 0.02,
      }),
      replayFrame,
      { blendFrames: 12, revealMotionAt: 0.55 },
    );
    const defensePlayers = blendArcadeReplayPlayersFromShell(
      context.formation.defense,
      applyArcadeReplayFrameToPlayers(context.formation.defense, replayFrame, context.direction, metrics, {
        movingThreshold: 0.02,
      }),
      replayFrame,
      { blendFrames: 12, revealMotionAt: 0.55 },
    );
    return {
      offensePlayers,
      defensePlayers,
      ball: ballPointForReplayFrame(
        replayFrame,
        context.direction,
        metrics,
        [...offensePlayers, ...defensePlayers],
      ),
    };
  });

  const kickoffFrames = buildKickoffLeadInFrames(context, replay, rawFrames, metrics);

  if (context.play?.sequence !== 1 || kickoffFrames.length === 0) {
    return kickoffFrames;
  }

  const firstFrame = kickoffFrames[0];
  const pauseFrames = Array.from({ length: OPENING_KICKOFF_PAUSE_FRAMES }, () => ({
    offensePlayers: firstFrame.offensePlayers.map((player: any) => ({ ...player, moving: false })),
    defensePlayers: firstFrame.defensePlayers.map((player: any) => ({ ...player, moving: false })),
    ball: { ...firstFrame.ball },
  }));

  return [...pauseFrames, ...kickoffFrames];
}
